
















































































































































import LazyHydrate from 'vue-lazy-hydration';
import {
  SfSelect,
  SfHeading,
  SfButton,
  SfLoader
} from '@storefront-ui/vue';
import {
  computed,
  defineComponent,
  onMounted,
  useContext,
  ref,
  ssrRef,
  useFetch,
  provide
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@wemade-vsf/cache';
import SkeletonLoader from 'components/theme/SkeletonLoader.vue';
import CategoryPagination from 'components/catalog/Category/CategoryPagination.vue';
import {
  useUiHelpers,
  useUiState,
} from '@wemade-vsf/composables';
import { useCategory } from '@wemade-vsf/catalog/lib/runtime/category/composables/useCategory';
import { useFacet } from '@wemade-vsf/catalog/lib/runtime/category/composables/useFacet';
import { useImage } from '@wemade-vsf/composables';
import { usePrice } from '@wemade-vsf/catalog/lib/runtime/pricing/usePrice';
import { useTraverseCategory } from '@wemade-vsf/catalog/lib/runtime/category/helpers/useTraverseCategory';
import facetGetters from '@wemade-vsf/catalog/lib/runtime/category/getters/facetGetters';
import { getMetaInfo, usePageStore, useConfigStore } from '@wemade-vsf/core';
import displayModesEnum from '@wemade-vsf/catalog/lib/runtime/category/enums/displayModesEnum';
import CategoryNavbar from 'components/catalog/Category/CategoryNavbar.vue';
import CategoryBreadcrumbs from 'components/catalog/Category/CategoryBreadcrumbs.vue';
import CategoryDescription from 'components/catalog/Category/CategoryDescription.vue';
import { useEvent } from '@wemade-vsf/composables';

import type { ProductInterface, Aggregation, CategoryTree } from '@wemade-vsf/magento-api';
import type { SortingModel } from '@wemade-vsf/catalog/lib/runtime/category/composables/useFacet/sortingOptions';
import type { Pagination } from '@wemade-vsf/core';

export default defineComponent({
  name: 'CategoryPage',
  components: {
    CategoryPagination,
    CategoryEmptyResults: () => import('components/catalog/Category/CategoryEmptyResults.vue'),
    CategoryFilters: () => import('components/catalog/Category/Filters/CategoryFilters.vue'),
    CategoryProductGrid: () => import('components/catalog/Category/CategoryProductGrid.vue'),
    CategoryProductList: () => import('components/catalog/Category/CategoryProductList.vue'),
    CategoryNavbar,
    CategoryBreadcrumbs,
    CategoryDescription,
    SfSelect,
    SfButton,
    LazyHydrate,
    SfHeading,
    SkeletonLoader,
    SfLoader
  },
  transition: 'fade',
  setup() {
    const { app: { $wm, $img } } = useContext();
    const { routeData } = usePageStore();
    const { loadCategoryDetails } = useCategory();
    const { getMagentoImage } = useImage()
    const { addTags } = useCache();
    const uiHelpers = useUiHelpers();

    const isShowCms = ref(false);
    const isShowProducts = ref(false);
    const products = ssrRef<ProductInterface[]>([]);
    const aggregations = ssrRef<Aggregation[]>([])
    const sortBy = ref<SortingModel>({ selected: '', options: [] });
    const pagination = ref<Pagination>({});

    const categoryLayout = $wm?.catalog?.categoryLayout ?? 'default';

    const productsLeft = computed(() => {
      if (!pagination.value?.totalItems || !pagination.value.currentPage || !pagination.value.itemsPerPage) return 0
      const leftProducts = pagination.value.totalItems - (pagination.value.currentPage * pagination.value.itemsPerPage)
      return leftProducts > 0 ? leftProducts : 0
    });

    const productContainerElement = ref<HTMLElement | null>(null);

    const {
      toggleFilterSidebar,
      changeToCategoryListView,
      changeToCategoryGridView,
      isCategoryGridView,
      isFilterSidebarOpen,
    } = useUiState();

    const categoryViewComponent = computed(() => isCategoryGridView.value ? 'CategoryProductGrid' : 'CategoryProductList');

    const closeFilterSidebar = () => {
      if (isFilterSidebarOpen) {
        toggleFilterSidebar()
      }
    }

    const { result, search, loadMoreAggregationOptions, loading: searchLoading } = useFacet();

    const categoryMeta = ref<CategoryTree | null>(null);

    const categoryHeaderStyle = ref<Record<string, string>>({'background-color': '#367b7b'})

    const { activeCategory, loadCategoryTree, categoryAncestors } = useTraverseCategory();
    const activeCategoryName = computed(() => activeCategory.value?.name ?? '');

    const categoryUid = routeData.uid;

    const initialDataLoaded = ref(false);

    const configStore = useConfigStore();

    const { fetch } = useFetch(async () => {
      if (!activeCategory.value) {
        await loadCategoryTree();
      }
      const categoryImage = ref<CategoryTree | null>(null);
      const categoryMetaData = await loadCategoryDetails({ category_uid: categoryUid });

      if (!categoryMetaData.image) {
        categoryImage.value = categoryAncestors.value.reverse().find(result => result.image);
      } else {
        categoryImage.value = categoryMetaData;
      }

      if (categoryMetaData) {
        categoryMeta.value = categoryMetaData;
        if (categoryImage.value.image) {
          categoryHeaderStyle.value = {
            'background-image': `url(${$img(getMagentoImage(categoryImage.value.image), { width: 1920, height: 723, fit: 'cover' })})`
          }
        }
        isShowCms.value = categoryMetaData.display_mode
          ? categoryMetaData.display_mode === displayModesEnum.PAGE || categoryMetaData.display_mode === displayModesEnum.PRODUCTS_AND_PAGE
          : true
        isShowProducts.value = categoryMetaData.display_mode
          ? categoryMetaData.display_mode === displayModesEnum.PRODUCTS || categoryMetaData.display_mode === displayModesEnum.PRODUCTS_AND_PAGE
          : true

        if (isShowProducts.value) {
          const configDefaultSort = configStore.storeConfig?.catalog_default_sort_by ? { sort: configStore.storeConfig.catalog_default_sort_by + '_ASC' } : {}
          const defaultSortBy = categoryMetaData.default_sort_by ? { sort: `${categoryMetaData.default_sort_by}_ASC` } : configDefaultSort;
          await search({ ...uiHelpers.getFacetsFromURL(defaultSortBy), category_uid: categoryUid });
          products.value = facetGetters.getProducts(result.value) ?? [];
          aggregations.value = facetGetters.getAggregations(result.value) ?? [];
          sortBy.value = facetGetters.getSortOptions(result.value);
          pagination.value = facetGetters.getPagination(result.value);
        }
      }
      initialDataLoaded.value = true

      const tags = [{ prefix: CacheTagPrefix.View, value: routeData.uid }];
      const productTags = products.value.map((product) => ({
        prefix: CacheTagPrefix.Product,
        value: product.uid,
      }));
      addTags([...tags, ...productTags]);
      if (!process.server && products.value.length) {
        useEvent('products:list', { products: products.value })
      }
    });

    const isPriceLoaded = ref(false);

    onMounted(async () => {
      const { getPricesBySku } = usePrice();
      if (products.value.length > 0) {
        const skus = products.value.map((item) => item.sku);
        const priceData = await getPricesBySku(skus, pagination.value.itemsPerPage);
        products.value = products.value.map((product) => {
          const priceRange = priceData.items.find((item) => item.sku === product.sku)?.price_range;

          if (priceRange) {
            return {
              ...product,
              price_range: priceRange,
            };
          }

          return { ...product };
        });
        useEvent('products:list', { products: products.value })
      }

      isPriceLoaded.value = true;
      initialDataLoaded.value = true
    });

    const loadNextPage = async () => {
      const currentPage = pagination.value?.currentPage || 1
      uiHelpers.changePage(currentPage + 1, false)
      await search({ ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid });
      products.value = [
        ...products.value,
        ...facetGetters.getProducts(result.value) ?? []
      ];
      pagination.value = facetGetters.getPagination(result.value);
    }

    const goToPage = (page: number) => {
      uiHelpers.changePage(page, false);
      fetch();
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      uiHelpers.changeItemsPerPage(itemsPerPage, false);
      goToPage(0);
    };

    const onReloadProducts = () => {
      goToPage(0);
      productContainerElement.value.scrollIntoView();
    };

    const loadMoreOptions = async (attributeCode) => {
      const aggIndex = aggregations.value?.findIndex(a => a.attribute_code === attributeCode);
      if (!aggIndex) return
      let result = await loadMoreAggregationOptions(attributeCode, { ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid });
      if (result) {
        let currentAggregations = aggregations.value;
        currentAggregations.splice(aggIndex, 1, result)
        aggregations.value = currentAggregations;
      }
    }

    provide('UseAggregationProvider', { loadMoreOptions })

    return {
      isPriceLoaded,
      ...uiHelpers,
      closeFilterSidebar,
      isCategoryGridView,
      categoryViewComponent,
      categoryLayout,
      changeToCategoryListView,
      changeToCategoryGridView,
      isFilterSidebarOpen,
      pagination,
      products,
      aggregations,
      sortBy,
      isShowCms,
      isShowProducts,
      activeCategoryName,
      routeData,
      doChangeItemsPerPage,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage,
      productsLeft,
      loadNextPage,
      searchLoading,
      initialDataLoaded,
      categoryHeaderStyle
    };
  },
  head() {
    const meta = getMetaInfo(this.categoryMeta);
    return {
      ...meta,
      bodyAttrs: {
        class: 'category-page header-light'
      }
    }
  },
});
